import {FormControl, FormField} from 'platform/components';
import {Grid, GridItem, Heading, VStack} from 'platform/foundation';

import {useWatch} from 'react-hook-form';

import {TenantResponseBody} from '@dms/api/shared';
import {basePriceSourceObject, GetArticleResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {CurrencyCodeType} from 'shared/web';

import {ArticleDetailForm} from '../types/ArticleDetailFormSchema';

interface PricesTabPricesFormProps {
  control: FormControl<ArticleDetailForm>;
  article: GetArticleResponse;
  tenant: TenantResponseBody;
  isPricingDisabled: boolean;
}

export function PricesTabPricesForm(props: PricesTabPricesFormProps) {
  const currency = props.tenant?.currency as CurrencyCodeType;
  const basePriceSource = useWatch({
    control: props.control,
    name: 'prices.pricesSettings.basePriceSource',
  });
  const isBasePriceDisabled = basePriceSource !== basePriceSourceObject.MANUAL;

  return (
    <>
      <VStack spacing={3}>
        <Heading size={4}>{i18n.t('general.labels.prices')}</Heading>
        {/* Base price */}
        <Grid columns={4}>
          <GridItem span={1}>
            <FormField
              control={props.control}
              isDisabled={isBasePriceDisabled || props.isPricingDisabled}
              type="currency"
              isAutoselectedOnFocus
              decimalPlaces={2}
              currency={currency}
              name="prices.dispensingPrices.basePriceWithoutVat"
              label={`${i18n.t('general.labels.basePrice')} ${i18n.t('general.labels.w/oVat')}`}
            />
          </GridItem>
          <GridItem span={1}>
            <FormField
              control={props.control}
              isDisabled
              type="currency"
              isAutoselectedOnFocus
              decimalPlaces={2}
              currency={currency}
              name="prices.dispensingPrices.basePriceWithVat"
              label={`${i18n.t('general.labels.basePrice')} ${i18n.t('general.labels.withVat')}`}
            />
          </GridItem>
        </Grid>
        {/* Sales base price */}
        <Grid columns={8}>
          <GridItem span={2}>
            <FormField
              isDisabled={props.isPricingDisabled}
              control={props.control}
              type="currency"
              isAutoselectedOnFocus
              decimalPlaces={2}
              currency={currency}
              name="prices.dispensingPrices.saleBasePriceWithoutVat"
              label={`${i18n.t('general.labels.salesBasePrice')} ${i18n.t('general.labels.w/oVat')}`}
            />
          </GridItem>
          <GridItem span={2}>
            <FormField
              isDisabled={props.isPricingDisabled}
              control={props.control}
              type="currency"
              isAutoselectedOnFocus
              decimalPlaces={2}
              currency={currency}
              name="prices.dispensingPrices.saleBasePriceWithVat"
              label={`${i18n.t('general.labels.salesBasePrice')} ${i18n.t('general.labels.withVat')}`}
            />
          </GridItem>
          <GridItem span={1}>
            <FormField
              isDisabled={props.isPricingDisabled}
              control={props.control}
              type="currency"
              isAutoselectedOnFocus
              name="prices.dispensingPrices.saleBaseProfit"
              label={i18n.t('general.labels.profit')}
            />
          </GridItem>
          <GridItem span={1}>
            <FormField
              isDisabled={props.isPricingDisabled}
              control={props.control}
              type="number"
              decimalPlaces={2}
              isStepperVisible
              name="prices.dispensingPrices.saleBaseMarginPercent"
              label={`${i18n.t('general.labels.margin')} (%)`}
            />
          </GridItem>
          <GridItem span={1}>
            <FormField
              isDisabled={props.isPricingDisabled}
              control={props.control}
              type="number"
              decimalPlaces={2}
              isStepperVisible
              name="prices.dispensingPrices.saleBaseMarkUpPercent"
              label={`${i18n.t('general.labels.markupPrice')} (%)`}
            />
          </GridItem>
        </Grid>
        {/* Warranty price */}
        <Grid columns={8}>
          <GridItem span={2}>
            <FormField
              isDisabled={props.isPricingDisabled}
              control={props.control}
              type="currency"
              isAutoselectedOnFocus
              currency={currency}
              decimalPlaces={2}
              name="prices.dispensingPrices.warrantyPriceWithoutVat"
              label={`${i18n.t('general.labels.warrantyPrice')} ${i18n.t('general.labels.w/oVat')}`}
            />
          </GridItem>
          <GridItem span={2}>
            <FormField
              isDisabled={props.isPricingDisabled}
              control={props.control}
              type="currency"
              isAutoselectedOnFocus
              currency={currency}
              decimalPlaces={2}
              name="prices.dispensingPrices.warrantyPriceWithVat"
              label={`${i18n.t('general.labels.warrantyPrice')} ${i18n.t('general.labels.withVat')}`}
            />
          </GridItem>
          <GridItem span={1}>
            <FormField
              isDisabled
              control={props.control}
              type="currency"
              isAutoselectedOnFocus
              name="prices.dispensingPrices.warrantyProfit"
              label={i18n.t('general.labels.profit')}
            />
          </GridItem>
          <GridItem span={1}>
            <FormField
              isDisabled
              control={props.control}
              type="number"
              decimalPlaces={2}
              isStepperVisible
              name="prices.dispensingPrices.warrantyMarginPercent"
              label={`${i18n.t('general.labels.margin')} (%)`}
            />
          </GridItem>
          <GridItem span={1}>
            <FormField
              isDisabled
              control={props.control}
              type="number"
              decimalPlaces={2}
              isStepperVisible
              name="prices.dispensingPrices.warrantyMarkUpPercent"
              label={`${i18n.t('general.labels.markupPrice')} (%)`}
            />
          </GridItem>
        </Grid>
      </VStack>
    </>
  );
}
